import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowLeft, User } from 'lucide-react';
import { MOCK_BLOG_POSTS } from '../data/mockData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const res = await fetch(`${API}/blog/${slug}`);
      if (res.ok) {
        const contentType = res.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const data = await res.json();
          if (data && typeof data === 'object' && data.slug) {
            setPost(data);
            return;
          }
        }
      }
      
      // Fallback
      console.warn(`API returned invalid response for blog post ${slug}. Using fallback data.`);
      const fallbackPost = MOCK_BLOG_POSTS.find(p => p.slug === slug);
      setPost(fallbackPost || null);
    } catch (error) {
      console.error('Failed to fetch post, using fallback:', error);
      const fallbackPost = MOCK_BLOG_POSTS.find(p => p.slug === slug);
      setPost(fallbackPost || null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080B14' }}>
        <div className="w-12 h-12 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#080B14' }}>
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Post Not Found</h2>
          <Link to="/blog" className="text-blue-400 hover:underline">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20" style={{ background: '#080B14' }}>
      <article className="max-w-4xl mx-auto px-6 lg:px-12">
        {/* Back Link */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="mb-8"
        >
          <Link 
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-blue-400 transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Blog
          </Link>
        </motion.div>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <span className="inline-block px-3 py-1 rounded-full bg-blue-500/20 text-blue-400 text-sm font-medium border border-blue-500/30 backdrop-blur-sm mb-4">
            {post.category}
          </span>
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-testid="blog-post-title">
            {post.title}
          </h1>
          <div className="flex items-center gap-6 text-gray-400 text-sm">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-blue-400" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-400" />
              <span>{formatDate(post.created_at)}</span>
            </div>
          </div>
        </motion.header>

        {/* Featured Image */}
        {post.image_url && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-12"
          >
            <img 
              src={post.image_url}
              alt={post.title}
              className="w-full rounded-2xl aspect-video object-cover opacity-95 shadow-2xl shadow-black/80"
              style={{ border: '1px solid rgba(99, 130, 255, 0.12)' }}
            />
          </motion.div>
        )}

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="prose prose-invert prose-lg max-w-none"
          style={{ color: '#94A3B8' }}
        >
          <p className="text-xl text-gray-200 mb-8 leading-relaxed font-light">
            {post.excerpt}
          </p>
          <div className="whitespace-pre-wrap leading-relaxed">
            {post.content}
          </div>
        </motion.div>
      </article>
    </div>
  );
}
